import { useQuery } from "@tanstack/react-query";
import {
	GetAllBusinessDomainsAsync,
	GetAllJobLocationsAsync,
	GetAllJobTypesAsync,
	GetAllLanguageProficienciesAsync,
	GetAllLanguagesAsync,
	GetAllRegionsAsync,
	GetAllSectorsAsync,
	GetAllSkillsAsync,
	GetDataProcessStagesByExpertiseExperienceIdAsync,
	GetExpertiseExperiencesAsync,
	GetFreelancingYearsOfExperiencesAsync,
	GetAllMissionsDurationsAsync,
	GetAllDocumentTypesAsync,
	GetAllUrlTypesAsync,
	GetAllExpertisesAsync,
	GetAllExperiencesAsync,
	GetAllToolsAsync,
	GetAllApplicationNoteSubjectsAsync
} from "../api/reference-data-api";

import { FreelancingYearsOfExperience } from "../models/FreelancingYearsOfExperience";
import { ExpertiseExperience } from "../models/ExpertiseExperience";
import { DataProcessStage } from "../models/DataProcessStage";
import { Language } from "../models/Language";
import { LanguageProficiency } from "../models/LanguageProficiency";
import { Region } from "../models/Region";
import { Sector } from "../models/Sector";
import { BusinessDomain } from "../models/BusinessDomain";
import { JobType } from "../models/JobType";
import { JobLocation } from "../models/JobLocation";
import { Skill } from "../models/Skill";
import { DocumentType } from "../models/DocumentType";
import { UrlType } from "../models/UrlType";
import { MissionsDuration } from "../models/MissionsDuration";
import { Expertise } from "../models/Expertise";
import { Experience } from "../models/Experience";
import { Tool } from "../models/Tool";
import { ApplicationNoteSubject } from "../models/ApplicationNoteSubject";

// Referential data rarely changes - cache for 10 minutes
const REFERENTIAL_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const REFERENTIAL_GC_TIME = 15 * 60 * 1000; // 15 minutes

export const useExpertiseExperiences = () => {
	return useQuery<ReadonlyArray<ExpertiseExperience>, Error>({
		queryKey: ["expertise-experiences"],
		queryFn: () => GetExpertiseExperiencesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useFreelancingYearsOfExperiences = () => {
	return useQuery<ReadonlyArray<FreelancingYearsOfExperience>, Error>({
		queryKey: ["freelancing-years-of-experiences"],
		queryFn: () => GetFreelancingYearsOfExperiencesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useDataProcessStages = (expertiseExperienceId: number | undefined) => {
	return useQuery<ReadonlyArray<DataProcessStage>, Error>({
		queryKey: ["data-process-stages", expertiseExperienceId],
		queryFn: () =>
			GetDataProcessStagesByExpertiseExperienceIdAsync(expertiseExperienceId),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useLanguages = () => {
	return useQuery<ReadonlyArray<Language>, Error>({
		queryKey: ["languages"],
		queryFn: () => GetAllLanguagesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useLanguageProficiencies = () => {
	return useQuery<ReadonlyArray<LanguageProficiency>, Error>({
		queryKey: ["language-proficiencies"],
		queryFn: () => GetAllLanguageProficienciesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useRegions = () => {
	return useQuery<ReadonlyArray<Region>, Error>({
		queryKey: ["regions"],
		queryFn: () => GetAllRegionsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useSectors = () => {
	return useQuery<ReadonlyArray<Sector>, Error>({
		queryKey: ["sectors"],
		queryFn: () => GetAllSectorsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useBusinessDomains = () => {
	return useQuery<ReadonlyArray<BusinessDomain>, Error>({
		queryKey: ["business-domains"],
		queryFn: () => GetAllBusinessDomainsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useJobTypes = () => {
	return useQuery<ReadonlyArray<JobType>, Error>({
		queryKey: ["job-types"],
		queryFn: () => GetAllJobTypesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useJobLocations = () => {
	return useQuery<ReadonlyArray<JobLocation>, Error>({
		queryKey: ["job-locations"],
		queryFn: () => GetAllJobLocationsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useSkills = () => {
	return useQuery<ReadonlyArray<Skill>, Error>({
		queryKey: ["skills"],
		queryFn: () => GetAllSkillsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useMissionsDurations = () => {
	return useQuery<ReadonlyArray<MissionsDuration>, Error>({
		queryKey: ["missions-durations"],
		queryFn: () => GetAllMissionsDurationsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useDocumentTypes = () => {
	return useQuery<ReadonlyArray<DocumentType>, Error>({
		queryKey: ["document-types"],
		queryFn: () => GetAllDocumentTypesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useUrlTypes = () => {
	return useQuery<ReadonlyArray<UrlType>, Error>({
		queryKey: ["url-types"],
		queryFn: () => GetAllUrlTypesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useExpertise = () => {
	return useQuery<ReadonlyArray<Expertise>, Error>({
		queryKey: ["expertises"],
		queryFn: () => GetAllExpertisesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useExperience = () => {
	return useQuery<ReadonlyArray<Experience>, Error>({
		queryKey: ["experiences"],
		queryFn: () => GetAllExperiencesAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useTools = () => {
	return useQuery<ReadonlyArray<Tool>, Error>({
		queryKey: ["tools"],
		queryFn: () => GetAllToolsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};

export const useApplicationNoteSubjects = () => {
	return useQuery<ReadonlyArray<ApplicationNoteSubject>, Error>({
		queryKey: ["application-note-subjects"],
		queryFn: () => GetAllApplicationNoteSubjectsAsync(),
		staleTime: REFERENTIAL_STALE_TIME,
		gcTime: REFERENTIAL_GC_TIME
	});
};
